import { useEffect } from 'react';
import './vol2.css';

const games = [
  {
    id: 'brilliant-flow',
    number: '01',
    title: 'Brilliant Flow',
    subtitle: 'Color Sort',
    description: 'Sort colorful jewels, control the conveyor, and complete satisfying pixel-art puzzles. Easy to learn, smart to master.',
    tags: ['Puzzle', 'Color Sort', 'Relaxing'],
    icon: '/vol2/assets/flow-icon.png',
    feature: '/vol2/assets/flow-feature.png',
    screens: ['/vol2/assets/flow-1.png', '/vol2/assets/flow-2.png', '/vol2/assets/flow-3.png'],
    link: 'https://play.google.com/store/apps/details?id=com.corelooppartners.BrilliantFlowColorSort',
    theme: 'violet',
  },
  {
    id: 'puzzy-cat',
    number: '02',
    title: 'Puzzy Cat',
    subtitle: 'Color Kitty Drop',
    description: 'Tap the boxes, guide the cats, and match every kitty with the right color. Cute on the outside, clever at heart.',
    tags: ['Puzzle', 'Cats', 'Casual'],
    icon: '/vol2/assets/cat-icon.png',
    feature: '/vol2/assets/cat-feature.png',
    screens: ['/vol2/assets/cat-1.png', '/vol2/assets/cat-2.png', '/vol2/assets/cat-3.png'],
    link: 'https://play.google.com/store/apps/details?id=com.CoreloopPartners.PuzzyCatColorKittyDrop',
    theme: 'pink',
  },
  {
    id: 'gas-please',
    number: '03',
    title: 'Gas Please!',
    subtitle: 'Idle Gas Station Tycoon',
    description: 'Pump fuel, stock the store, hire a crew, and grow a tiny roadside stop into a busy service empire.',
    tags: ['Idle', 'Tycoon', 'Arcade'],
    icon: '/vol2/assets/gas-icon.png',
    feature: '/vol2/assets/gas-feature.png',
    screens: ['/vol2/assets/gas-1.png', '/vol2/assets/gas-2.png', '/vol2/assets/gas-3.png'],
    link: 'https://play.google.com/store/apps/details?id=com.CoreloopGarage.GasPlease',
    theme: 'blue',
  },
];

function PlayButton({ href, label = 'Get it on Google Play' }: { href: string; label?: string }) {
  return (
    <a className="v2-play-button" href={href} target="_blank" rel="noreferrer">
      <i className="ri-google-play-fill" aria-hidden="true" />
      <span>{label}</span>
      <i className="ri-arrow-right-up-line" aria-hidden="true" />
    </a>
  );
}

export default function Vol2() {
  useEffect(() => {
    const oldTitle = document.title;
    document.title = 'Coreloop Partners — Mobile Game Studio';
    return () => { document.title = oldTitle; };
  }, []);

  return (
    <main className="v2-site">
      <header className="v2-header">
        <a className="v2-logo" href="#top" aria-label="Coreloop Partners home">
          <span className="v2-logo-mark"><b /> <b /> <b /></span>
          <span>Coreloop<br /><strong>Partners</strong></span>
        </a>
        <nav className="v2-nav" aria-label="Main navigation">
          <a href="#about">About</a>
          <a href="#games">Games</a>
          <a href="#contact">Contact</a>
        </nav>
        <a className="v2-header-cta" href="#games">Play our games <i className="ri-arrow-down-line" /></a>
      </header>

      <section className="v2-hero" id="top">
        <div className="v2-hero-copy">
          <p className="v2-eyebrow"><span /> Mobile games from Türkiye</p>
          <h1>Small screens.<br /><em>Big play.</em></h1>
          <p className="v2-lead">We make bright, easy-to-learn mobile games built around one strong idea: you should want one more level.</p>
          <div className="v2-hero-actions">
            <a className="v2-primary-button" href="#games">Explore our games <i className="ri-arrow-right-line" /></a>
            <a className="v2-text-link" href="#about">Meet the studio <i className="ri-arrow-down-line" /></a>
          </div>
          <div className="v2-hero-stats" aria-label="Studio highlights">
            <div><strong>3</strong><span>Games on<br />Google Play</span></div>
            <div><strong>1</strong><span>Simple loop<br />at a time</span></div>
            <div><strong>∞</strong><span>Ideas left<br />to play</span></div>
          </div>
        </div>

        <div className="v2-hero-art" aria-label="Coreloop Partners game artwork">
          <div className="v2-orbit v2-orbit-one" />
          <div className="v2-orbit v2-orbit-two" />
          <img className="v2-hero-feature v2-feature-flow" src="/vol2/assets/flow-feature.png" alt="Brilliant Flow game artwork" />
          <img className="v2-hero-feature v2-feature-cat" src="/vol2/assets/cat-feature.png" alt="Puzzy Cat game artwork" />
          <img className="v2-hero-icon v2-icon-gas" src="/vol2/assets/gas-icon.png" alt="Gas Please game icon" />
          <img className="v2-hero-icon v2-icon-cat" src="/vol2/assets/cat-icon.png" alt="Puzzy Cat game icon" />
          <span className="v2-float-label v2-label-one">Sort it</span>
          <span className="v2-float-label v2-label-two">Build it</span>
        </div>
      </section>

      <div className="v2-marquee" aria-label="What we make">
        <div>
          <span>Colorful puzzles</span><b>✦</b><span>Cozy worlds</span><b>✦</b><span>Clear game loops</span><b>✦</b><span>One more level</span><b>✦</b>
          <span>Colorful puzzles</span><b>✦</b><span>Cozy worlds</span><b>✦</b><span>Clear game loops</span><b>✦</b><span>One more level</span><b>✦</b>
        </div>
      </div>

      <section className="v2-about" id="about">
        <div className="v2-section-label">About Coreloop</div>
        <div className="v2-about-grid">
          <div>
            <h2>Simple ideas.<br /><em>Playful execution.</em></h2>
            <p>Coreloop Partners is a mobile game studio focused on readable mechanics, friendly art, and satisfying progression. We build the core loop first, prove the fun, then add the polish players feel in every tap.</p>
            <a className="v2-text-link dark" href="#games">See what we made <i className="ri-arrow-right-line" /></a>
          </div>
          <div className="v2-values">
            <article><span>01</span><i className="ri-focus-3-line" /><h3>Clear from the first tap</h3><p>No manual needed. The best mobile ideas explain themselves through play.</p></article>
            <article><span>02</span><i className="ri-sparkling-2-line" /><h3>Polish with a purpose</h3><p>Juice, sound, and motion support the loop instead of hiding it.</p></article>
            <article><span>03</span><i className="ri-refresh-line" /><h3>Built for one more go</h3><p>Short sessions, visible progress, and satisfying decisions keep play moving.</p></article>
          </div>
        </div>
      </section>

      <section className="v2-games" id="games">
        <div className="v2-games-heading">
          <div className="v2-section-label">Our games</div>
          <h2>Three worlds.<br />Pick your loop.</h2>
          <p>Puzzles, cats, jewels, and a very busy gas station. All free to explore on Google Play.</p>
        </div>

        <div className="v2-game-list">
          {games.map((game) => (
            <article className={`v2-game-card ${game.theme}`} id={game.id} key={game.id}>
              <div className="v2-game-info">
                <span className="v2-game-number">{game.number}</span>
                <img className="v2-game-icon" src={game.icon} alt={`${game.title} icon`} loading="lazy" />
                <p className="v2-game-kicker">Coreloop original</p>
                <h3>{game.title}<small>{game.subtitle}</small></h3>
                <p className="v2-game-description">{game.description}</p>
                <ul>{game.tags.map((tag) => <li key={tag}>{tag}</li>)}</ul>
                <PlayButton href={game.link} />
              </div>
              <div className="v2-game-gallery">
                <img className="v2-game-feature" src={game.feature} alt={`${game.title} feature artwork`} loading="lazy" />
                <div className="v2-phone-row">
                  {game.screens.map((screen, index) => (
                    <img src={screen} alt={`${game.title} gameplay screenshot ${index + 1}`} loading="lazy" key={screen} />
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="v2-contact" id="contact">
        <div>
          <p className="v2-eyebrow light"><span /> Say hello</p>
          <h2>Have a game<br />in mind?</h2>
        </div>
        <div className="v2-contact-copy">
          <p>We are always open to publishing conversations, creative partnerships, and good game ideas.</p>
          <a href="mailto:info@corelooppartners.com">info@corelooppartners.com <i className="ri-arrow-right-up-line" /></a>
        </div>
      </section>

      <footer className="v2-footer">
        <a className="v2-logo footer" href="#top">
          <span className="v2-logo-mark"><b /> <b /> <b /></span>
          <span>Coreloop <strong>Partners</strong></span>
        </a>
        <p>Mobile games made in Türkiye.</p>
        <p>© {new Date().getFullYear()} Coreloop Partners</p>
      </footer>
    </main>
  );
}
